import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpenCheck,
  Building2,
  CalendarDays,
  Download,
  FileText,
  ListTree,
  Printer,
  Scale,
  Search,
  ShieldCheck,
} from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import DebouncedSearchInput from '../components/UI/DebouncedSearchInput';
import { InfoBadge } from '../components/UI/InfoBadge';
import { Block } from '../components/handbook/DocumentBlocks';
import {
  useDownloadTrainingMaterialPdf,
  useTrainingMaterial,
} from '../hooks/useTrainingMaterialsData';
import { filterDocumentSections } from '../utils/documentSectionUtils';

const EMPTY_SECTIONS = [];

const CATEGORY = {
  compliance_ethics: {
    label: 'Compliance & Ethics',
    icon: Scale,
    text: 'text-violet-700',
    soft: 'bg-violet-50',
  },
  workplace: {
    label: 'Workplace Awareness',
    icon: BookOpenCheck,
    text: 'text-teal-700',
    soft: 'bg-teal-50',
  },
  safety: {
    label: 'Safety',
    icon: ShieldCheck,
    text: 'text-amber-700',
    soft: 'bg-amber-50',
  },
  information_security: {
    label: 'Information Security',
    icon: ShieldCheck,
    text: 'text-sky-700',
    soft: 'bg-sky-50',
  },
  general: {
    label: 'General',
    icon: BookOpenCheck,
    text: 'text-blue-700',
    soft: 'bg-blue-50',
  },
};

const ContentsList = ({ sections, activeId, onNavigate }) => (
  <nav aria-label="Training material contents">
    <ol className="space-y-1">
      {sections.map((section) => {
        const active = section.id === activeId;
        return (
          <li key={section.id}>
            <button
              type="button"
              onClick={() => onNavigate(section.id)}
              className={`w-full flex items-start gap-2 text-left px-2 py-2 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 ${
                active
                  ? 'bg-blue-50 text-blue-800 font-medium'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <span className={`w-6 flex-shrink-0 text-xs tabular-nums mt-0.5 ${active ? 'text-blue-700' : 'text-gray-400'}`}>
                {section.number || ''}
              </span>
              <span className="text-sm leading-snug">{section.title}</span>
            </button>
          </li>
        );
      })}
    </ol>
  </nav>
);

const SelfServiceTrainingMaterial = () => {
  const { materialKey } = useParams();
  const { data: material, isLoading, isError } = useTrainingMaterial(materialKey);
  const downloadPdf = useDownloadTrainingMaterialPdf();
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState('');
  const sectionRefs = useRef({});

  const sections = material?.sections || EMPTY_SECTIONS;
  const filteredSections = useMemo(
    () => filterDocumentSections(sections, query),
    [query, sections],
  );

  const scrollToSection = useCallback((sectionId) => {
    const section = sectionRefs.current[sectionId];
    if (!section) return;

    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(sectionId);
    if (window.history?.replaceState) {
      window.history.replaceState(null, '', `#${sectionId}`);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-96px 0px -65% 0px', threshold: 0 },
    );

    filteredSections.forEach((section) => {
      const element = sectionRefs.current[section.id];
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [filteredSections]);

  useEffect(() => {
    if (!material) return;
    const sectionId = window.location.hash.replace('#', '');
    if (sectionId && sectionRefs.current[sectionId]) {
      const timer = window.setTimeout(() => scrollToSection(sectionId), 100);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [material, scrollToSection]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="card">
          <div className="card-content py-20"><LoadingSpinner /></div>
        </div>
      </DashboardLayout>
    );
  }

  if (isError || !material) {
    return (
      <DashboardLayout>
        <div className="card">
          <div className="card-content text-center py-16">
            <FileText className="w-8 h-8 text-gray-300 mx-auto mb-3" aria-hidden="true" />
            <h1 className="text-subsection-header">Training material not found</h1>
            <p className="text-body mt-1">This resource may have been moved or unpublished.</p>
            <Link
              to="/self-service/training-materials"
              className="btn btn-primary mt-4 mx-auto inline-flex"
            >
              Back to Training Materials
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const category = CATEGORY[material.category] || CATEGORY.general;
  const CategoryIcon = category.icon;
  const resultCount = filteredSections.length;

  const metadata = [
    {
      icon: CalendarDays,
      label: 'Training period',
      value: material.trainingPeriod || 'Not specified',
    },
    {
      icon: Building2,
      label: 'Issued by',
      value: material.issuingOrganisation || 'Not specified',
    },
    {
      icon: FileText,
      label: 'Source',
      value: material.sourcePageCount
        ? `${material.sourcePageCount} pages`
        : 'Training material',
    },
    {
      icon: CalendarDays,
      label: 'Portal review',
      value: material.reviewedOn || 'Not specified',
    },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen pb-16">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex items-start gap-4">
              <Link
                to="/self-service/training-materials"
                aria-label="Back to Training Materials"
                title="Back to Training Materials"
                className="icon-button flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" aria-hidden="true" />
              </Link>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${category.soft} ${category.text}`}>
                    <CategoryIcon className="w-3.5 h-3.5" aria-hidden="true" />
                    {category.label}
                  </span>
                  <InfoBadge
                    color="slate"
                    label={material.classification}
                    title={`${material.classification} — for company use only`}
                  />
                </div>
                <h1 className="text-section-header mt-3">{material.title}</h1>
                {material.subtitle && (
                  <p className="text-sm font-medium text-gray-600 mt-1">{material.subtitle}</p>
                )}
                <p className="text-body mt-2 max-w-4xl">{material.summary}</p>
              </div>
            </div>

            <dl className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
              {metadata.map((item) => (
                <div key={item.label} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5">
                  <dt className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                    <item.icon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                    {item.label}
                  </dt>
                  <dd
                    className="text-sm font-semibold text-gray-900 mt-0.5 truncate"
                    title={item.value}
                  >
                    {item.value}
                  </dd>
                </div>
              ))}
            </dl>

            <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <DebouncedSearchInput
                value={query}
                onSearch={setQuery}
                placeholder="Search this material..."
                theme="employee"
                className="w-full max-w-md"
              />
              <div className="flex flex-wrap gap-3">
                {material.hasPdf && (
                  <button
                    type="button"
                    onClick={() => downloadPdf.mutate({
                      materialKey: material.key,
                      filename: material.sourceFilename,
                    })}
                    className="btn btn-secondary"
                    disabled={downloadPdf.isPending}
                  >
                    <Download className="w-4 h-4" aria-hidden="true" />
                    {downloadPdf.isPending ? 'Preparing…' : 'Download source PDF'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="btn btn-secondary"
                >
                  <Printer className="w-4 h-4" aria-hidden="true" />
                  Print / Save PDF
                </button>
              </div>
            </div>

            {query && (
              <p className="text-small mt-2" role="status">
                {resultCount} section{resultCount === 1 ? '' : 's'} found
              </p>
            )}
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
          <BookOpenCheck className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <h2 className="text-sm font-semibold text-blue-900">Read at your own pace</h2>
            <p className="text-sm leading-relaxed text-blue-800 mt-0.5">
              This is an employee reference resource. No quiz or acknowledgement is required on this page.
            </p>
          </div>
        </div>

        {resultCount > 0 && (
          <div className="lg:hidden card sticky top-2 z-20">
            <div className="card-content py-3">
              <label htmlFor="training-material-section" className="block text-xs font-medium text-gray-600 mb-1.5">
                Jump to section
              </label>
              <select
                id="training-material-section"
                className="select w-full"
                value={activeId}
                onChange={(event) => scrollToSection(event.target.value)}
              >
                <option value="">Choose a section</option>
                {filteredSections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.number ? `${section.number}. ` : ''}{section.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="lg:grid lg:grid-cols-[280px,1fr] lg:gap-8 lg:items-start">
          <aside className="hidden lg:block lg:sticky lg:top-6 lg:self-start">
            <div className="card flex flex-col max-h-[calc(100vh-3rem)]">
              <div className="card-content flex flex-col min-h-0 flex-1">
                <div className="flex items-center gap-2 mb-4 flex-shrink-0">
                  <ListTree className="w-4 h-4 text-blue-800" aria-hidden="true" />
                  <h2 className="text-card-header">Contents</h2>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain -mr-2 pr-2">
                  <ContentsList
                    sections={filteredSections}
                    activeId={activeId}
                    onNavigate={scrollToSection}
                  />
                </div>
              </div>
            </div>
          </aside>

          <main className="space-y-6 mt-6 lg:mt-0">
            {resultCount === 0 ? (
              <div className="card">
                <div className="card-content text-center py-12">
                  <Search className="w-8 h-8 text-gray-300 mx-auto mb-3" aria-hidden="true" />
                  <h2 className="text-subsection-header">No matching sections</h2>
                  <p className="text-body mt-1">Try another keyword or clear the search.</p>
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="btn btn-primary mt-4 mx-auto"
                  >
                    Clear search
                  </button>
                </div>
              </div>
            ) : (
              filteredSections.map((section) => (
                <article
                  key={section.id}
                  id={section.id}
                  ref={(element) => { sectionRefs.current[section.id] = element; }}
                  className="card scroll-mt-24"
                >
                  <div className="card-content">
                    <header className="flex items-start gap-3 pb-4 mb-4 border-b border-gray-100">
                      <span className={`flex-shrink-0 w-9 h-9 rounded-full ${category.soft} ${category.text} font-bold text-sm flex items-center justify-center tabular-nums`}>
                        {section.number || '•'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-subsection-header">{section.title}</h2>
                        {section.summary && (
                          <p className="text-small mt-1">{section.summary}</p>
                        )}
                      </div>
                    </header>
                    <div className="space-y-4">
                      {(section.blocks || []).map((block, index) => (
                        <Block key={index} block={block} />
                      ))}
                    </div>
                  </div>
                </article>
              ))
            )}

            {!query && (
              <article className="card">
                <div className="card-content">
                  <header className="flex items-start gap-3 pb-4 mb-4 border-b border-gray-100">
                    <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
                      <ShieldCheck className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <div>
                      <h2 className="text-subsection-header">Related employee resources</h2>
                      <p className="text-small mt-1">Use the formal policy library or reporting channel when you need to take action.</p>
                    </div>
                  </header>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/self-service/policies"
                      className="btn btn-secondary self-start sm:self-auto"
                    >
                      Company Policies
                    </Link>
                    <Link
                      to="/self-service/whistleblowing"
                      className="btn btn-secondary self-start sm:self-auto"
                    >
                      Report a concern
                    </Link>
                  </div>
                  {material.issuingDepartment && (
                    <p className="text-small mt-4">
                      Source department: {material.issuingDepartment}
                      {material.documentRef ? ` · ${material.documentRef}` : ''}
                    </p>
                  )}
                </div>
              </article>
            )}
          </main>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SelfServiceTrainingMaterial;

